import React from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import { cn } from '@rocco/utils/cn';
import {
    Flex,
    ReadMore,
    useStyles,
    pxToRem,
    pxArrayToRem,
    Grid,
} from '@archipro-design/aria';
import * as S from './Content.style';
import { useIsSafari } from '@modules/root/hook/use-is-safari';
import { ArrowRightLine } from '@archipro-design/icons';
import Stats from './Stats';
import CompanyInfoStats from './CompanyInfoStats';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import type { loader } from '~/routes/_app.product.$urlSegment';
import type { ProductProfessional } from '../../type/product-professional';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

const MAX_LINES = 9;

export type ContentData = Pick<
    ProductProfessional,
    | 'ID'
    | 'Title'
    | 'BlockSummary'
    | 'URLSegment'
    | 'LibraryLocations'
    | 'ProductCount'
    | 'EnquiryResponseRate'
    | 'EnquiryAvgResponseHours'
    | 'HasArticles'
    | 'HasPartners'
    | 'HasProjects'
    | 'HasVideos'
>;

interface ContentProps {
    data: ContentData;
}

interface ListProps {
    label: string;
    link: string;
}

const Content = ({ data }: ContentProps) => {
    const [visible, setVisible] = React.useState(false);
    const isSafari = useIsSafari();
    const { css } = useStyles({ isSafari, visible });
    const links = generateLinks(data);
    const { product } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: product.Professional.ID,
        professionalTitle: product.Professional.Title,
        itemType: 'Product',
    });

    return (
        <Flex column={false} vAlign="center">
            <Grid
                columns={pxArrayToRem([880, 896])}
                className={css({
                    columnGap: pxToRem(44),
                })}
            >
                <div>
                    <div
                        onMouseEnter={() => setVisible(true)}
                        onMouseLeave={() => setVisible(false)}
                        className="mb-[8rem] block w-[27.125rem]"
                    >
                        {links.map((link) => (
                            <Link
                                to={link.link}
                                key={link.label}
                                prefetch="intent"
                                className={cn(
                                    'group mb-0 flex h-[3.375rem] w-full items-center border-b transition-colors duration-300',
                                    visible
                                        ? 'border-black/50 hover:border-black'
                                        : 'border-black hover:border-black/50',
                                )}
                            >
                                <span
                                    className={cn(
                                        'text-charcoal-000 flex-1 text-[1.25rem] tracking-[-0.01em] transition-opacity duration-300',
                                        visible
                                            ? 'opacity-50 group-hover:opacity-100'
                                            : 'opacity-100 group-hover:opacity-50',
                                    )}
                                >
                                    {link.label}
                                </span>
                                <ArrowRightLine className="relative -bottom-[0.0625rem] mr-[0.5rem] h-[1.875rem] w-[1.875rem] opacity-0 transition-[opacity,margin] duration-300 ease-in-out group-hover:mr-0 group-hover:opacity-100" />
                            </Link>
                        ))}
                    </div>

                    <Stats data={data} />
                </div>

                <div className={css(S.AboutTextContainer)}>
                    <ReadMore
                        content={{
                            children: (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: data.BlockSummary ?? '',
                                    }}
                                    onClick={clickProxy}
                                    onContextMenu={clickProxy}
                                />
                            ),
                        }}
                        maxLines={MAX_LINES}
                        className={css(S.AboutTextContent)}
                        variables={{
                            toggleFontSize: pxToRem(16),
                        }}
                    />
                    <CompanyInfoStats data={data} />
                </div>
            </Grid>
        </Flex>
    );
};

const generateLinks = (data: ContentData): ListProps[] => {
    const BASE_COMPANY_URL = `/professional/${data.URLSegment}`;
    return [
        {
            label: 'Overview',
            link: BASE_COMPANY_URL,
        },
        {
            label: 'About',
            link: `${BASE_COMPANY_URL}/info#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        {
            label: 'Products',
            link: `${BASE_COMPANY_URL}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        ...(data.HasProjects
            ? [
                  {
                      label: 'Projects',
                      link: `${BASE_COMPANY_URL}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasArticles
            ? [
                  {
                      label: 'Articles',
                      link: `${BASE_COMPANY_URL}/journal#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasVideos
            ? [
                  {
                      label: 'Videos',
                      link: `${BASE_COMPANY_URL}/videos#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasPartners
            ? [
                  {
                      label: 'Network',
                      link: `${BASE_COMPANY_URL}/partners#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        {
            label: 'Contact',
            link: `${BASE_COMPANY_URL}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
    ];
};

export default Content;
